/**
 * What the Foundation's site asks of `app_api`.
 *
 * **The first thing this app reads from the API**, as of 2026-09-25: the appeals, which were
 * two hand-written records in `data/donations.ts` until Steward could write them, and the
 * giving form, which posted nowhere. Both are unauthenticated — a donor has no account — so
 * there is no `AuthProvider` here and no token; the calls are plain `fetch`.
 *
 * Every failure carries the API's own `detail` line through `ApiError`, the same class the
 * other front ends throw, so a message the server wrote to be read is what the page shows.
 */

import { ApiError, HONEYPOT_FIELD, type Text } from '@kkf/design'
import { API_ROOT } from '../data/links'

/** An appeal as `GET /v1/donations/appeals` returns it. Mirrors `AppealPublic`. */
export interface ApiAppeal {
  slug: string
  /** `active` or `closed`. The API never returns a draft. */
  status: string
  tag: Text
  title: Text
  summary: Text
  body: Text[]
  image: string
  images?: string[] | null
  image_alt?: Text | null
  video?: string | null
  /** Money is a decimal string on the wire — `"2000.00"`. Never summed here. */
  goal_amount: string
  currency: string
  /** Summed by the API from **completed** payments. `"0.00"` until money arrives. */
  raised_amount: string
  gift_count: number
}

/** What the giving form sends. Mirrors `GiftCreate`, anti-spam fields included. */
export interface GiftBody {
  appeal: string
  amount: string
  first_name?: string
  last_name?: string
  email: string
  anonymous: boolean
  note?: string
  language: string
  [HONEYPOT_FIELD]?: string
  verification?: string
}

/** What the donor is told. Mirrors `GiftReceived` — a reference and a sentence, not the record. */
export interface GiftReceived {
  detail: string
  reference: string
  amount: string
  currency: string
}

async function send<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
  const response = await fetch(`${API_ROOT}${path}`, {
    method,
    headers: {
      Accept: 'application/json',
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      // Which site the request came from. The API answers as the Foundation when it is
      // absent; it is set anyway, so a gift's `source_app` is a statement and not a default.
      'X-App-Id': 'foundation',
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (!response.ok) {
    let parsed: { detail?: string } | null = null
    try {
      parsed = (await response.json()) as { detail?: string }
    } catch {
      // A proxy answering with HTML must not become a second, more confusing error.
      parsed = null
    }
    throw new ApiError(response.status, parsed as never)
  }
  return (await response.json()) as T
}

/** Every appeal a visitor may see, in the directory's order. */
export const fetchAppeals = (): Promise<ApiAppeal[]> => send<ApiAppeal[]>('GET', '/donations/appeals')

/**
 * Record a gift. **Nothing is charged** — the API stores a pledge and a PayPal payment with no
 * order behind it, and says so in `detail`. The page must show its confirmation only once
 * this resolves: a donor who believes a gift went through stops trying to give it.
 */
export const submitGift = (body: GiftBody): Promise<GiftReceived> =>
  send<GiftReceived>('POST', '/donations/gifts', body)
